import dynamic from 'next/dynamic';

import { AnnouncementBarWithBuilder } from 'layout/AnnouncementBar';
import DraftPreviewBadge from 'layout/DraftPreviewBadge';
import Head from 'layout/Head';
import { FooterWithBuilder } from 'layout/footer';
import { HeaderWithBuilder } from 'layout/header';

import AttributionManager from 'utils/layout/AttributionManager';
import LazyRudderStackScript from 'utils/layout/LazyRudderStackScript';
import LazySegmentScript from 'utils/layout/LazySegmentScript';
import { useImmersiveChromeActive } from 'utils/layout/useImmersiveChrome';
import { extractG2Override } from 'utils/schema/organizationSchema';
import { TrackConvertingPage } from 'utils/trackPage';

import type { Content } from '@builder.io/react';
import type { FC } from 'react';
import type { FooterVariant, HeaderVariant, LayoutDataProps, ReactChildren } from 'types/global';

const ConsentManager = dynamic(() => import('molecules/ConsentManager'), { ssr: false });
const ConnectFooter = dynamic(() => import('components/ConnectFooter'));
const HeaderLandingWithBuilder = dynamic(() =>
  import('layout/Landing/header/HeaderLanding').then(mod => ({ default: mod.HeaderLandingWithBuilder })),
);

interface GlobalLayoutProps extends ReactChildren, LayoutDataProps {
  /**
   * The SEO data for the page.
   */
  seoData: Content;
  /**
   * Indicates whether the layout is being previewed.
   */
  preview?: boolean;
  /**
   * Hides the global header when set to 'hidden'. Used by immersive pages like the Free Trial FTUX.
   */
  headerVariant?: HeaderVariant;
  /**
   * Hides the global footer when set to 'hidden'. Used by immersive pages like the Free Trial FTUX.
   */
  footerVariant?: FooterVariant;
}

const GlobalLayout: FC<GlobalLayoutProps> = ({
  children,
  seoData,
  headerContent,
  footerContent,
  announcementBarContent,
  preview,
  // Set by fetchLayoutModels when the chrome was fetched as a draft under
  // Next.js Preview Mode; every page spreads layoutData into this layout, so
  // the indicator needs no per-page prop threading.
  draftPreview,
  headerVariant,
  footerVariant,
}) => {
  // An immersive component mounted anywhere in the tree (e.g. a Dashboard
  // Backdrop or Free Trial FTUX in a client-swapped A/B variation, or the
  // Builder editor preview) hides the chrome even when derivePageChrome only
  // saw the non-immersive parent variation on the server.
  const immersiveActive = useImmersiveChromeActive();
  const headerHidden = headerVariant === 'hidden' || immersiveActive;
  const footerHidden = footerVariant === 'hidden' || immersiveActive;
  // 'default' (or unset) keeps the full standard site header; any other variant
  // (connect, centeredLogoNoDemo, logoFreeTrial, standardHeader) is delegated to
  // the landing header renderer so the page model supports the same variants as lp.
  const isStandardHeader = !headerHidden && (!headerVariant || headerVariant === 'default');
  const useHeaderVariant = !headerHidden && !!headerVariant && headerVariant !== 'default';
  const isConnectFooter = footerVariant === 'connect' && !footerHidden;

  // The editable G2 rating lives on the footers global (already fetched), so the
  // Organization schema in Head can use it without an extra Builder call.
  const g2Override = extractG2Override((footerContent as Content | undefined)?.data);

  // The Connect chrome uses a sticky footer: a full-height flex column so the
  // footer snaps to the bottom of the viewport on short pages (e.g. the
  // thank-you page) instead of sitting under the skinny content.
  const chrome = (
    <>
      {isStandardHeader && <AnnouncementBarWithBuilder content={announcementBarContent} preview={preview} />}
      {isStandardHeader && <HeaderWithBuilder content={headerContent} preview={preview} />}
      {useHeaderVariant && (
        <HeaderLandingWithBuilder content={headerContent} preview={preview} variant={headerVariant} />
      )}
      <main className={isConnectFooter ? 'flex-1' : undefined}>{children}</main>
      {isConnectFooter ? (
        <ConnectFooter />
      ) : (
        !footerHidden && <FooterWithBuilder content={footerContent} preview={preview} />
      )}
    </>
  );

  return (
    <>
      <Head {...(seoData as unknown as Record<string, unknown>)} g2Override={g2Override} />
      <AttributionManager />
      <LazySegmentScript />
      <LazyRudderStackScript />
      <TrackConvertingPage />
      {isConnectFooter ? <div className="flex min-h-screen flex-col">{chrome}</div> : chrome}
      {draftPreview && <DraftPreviewBadge />}
      <ConsentManager />
    </>
  );
};

export default GlobalLayout;
